import { motion } from 'framer-motion'
import { Link } from 'gatsby'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import StickyBox from 'react-sticky-box'
import { useMeasure, useWindowSize } from 'react-use'
import { useLink } from '../../hooks/useLink'
import { useToolOverlay } from '../Context/ToolOverlayContext'

const IconTable = ({ items, isActive }) => {
  return (
    <div className="divide-y divide-gray-4 mb-6">
      {items.map(item => (
        <div key={item.title} className="flex items-center gap-1.5 py-2">
          <img
            src={item.featureImage?.[0]?.url}
            alt=""
            className={`w-5.5 h-auto transition-opacity ${
              !isActive && 'opacity-20'
            }`}
          />
          <div>{item.title}</div>
        </div>
      ))}
    </div>
  )
}

const CardContent = ({ variants, activeIndex }) => {
  return (
    <>
      {variants.map((variant, index) => (
        <div key={variant.id}>
          <div
            className={`px-7 pt-7 mb-7 pb-5 sticky bg-white top-0 rounded-5 transition-all z-50 ${
              index === activeIndex ? 'text-black' : 'text-gray-4'
            }`}
            id={`model-${index}`}
          >
            <h1 className="u-h2">{variant.variantName}</h1>
            <div
              className="absolute top-full left-0 right-0 h-7 flex items-start justify-center bg-contain bg-top"
              style={{
                backgroundImage: 'url(/mid-shadow.png)',
                backgroundSize: '100% 100%',
              }}
            />
          </div>

          <div
            className={`px-7 pb-7 ${
              index === activeIndex ? 'text-black' : 'text-gray-4'
            }`}
            style={{ minHeight: index === variants.length - 1 ? '600px' : 0 }}
          >
            <IconTable
              items={variant.features}
              isActive={index === activeIndex}
            />
            <div className="sm:hidden flex items-center gap-2 pb-6">
              <ButtonLink link={variant.button1} />
              <ButtonLink link={variant.button2} />
            </div>
            <div
              className="u-p mb-7"
              dangerouslySetInnerHTML={{ __html: variant.description }}
            />
          </div>
        </div>
      ))}
    </>
  )
}

const ButtonLink = ({ link: data }) => {
  const { url, label } = useLink(data)
  const toolOverlay = useToolOverlay()

  if (url.endsWith('#sidebar-plz')) {
    return <button type="button" className="u-btn justify-center whitespace-nowrap" onClick={() => {
      toolOverlay.setContextSectionLimit('people')
      toolOverlay.setToolOverlayOpen(true)
    }}>
      {label}
    </button>
  }

  return (
    <Link to={url} className="u-btn justify-center whitespace-nowrap">
      {label}
    </Link>
  )
}

const M26VariantConfigurator = ({ intro, variants }) => {
  const [introRef, { height: introHeight }] = useMeasure()
  const [imageRef, { height: imageHeight }] = useMeasure()
  const { width: windowWidth, height: windowHeight } = useWindowSize()

  const [activeModelIndex, setActiveModelIndex] = useState(0)

  const imageContainerRef = useRef()
  const scrollContentRef = useRef()
  const scrollTargetRef = useRef()

  const isMobile = windowWidth < 640
  const isTablet = windowWidth < 1024

  const scrollContentRightPixels =
    scrollContentRef.current?.getBoundingClientRect().right
  const imageLeftPixels =
    imageContainerRef.current?.getBoundingClientRect().left
  const buttonsLeftMargin = Number.isNaN(
    scrollContentRightPixels - imageLeftPixels,
  )
    ? 0
    : scrollContentRightPixels - imageLeftPixels

  const onScroll = () => {
    const scrollContentTop =
      scrollContentRef.current?.getBoundingClientRect().top
    const targetContentTop =
      scrollTargetRef.current?.getBoundingClientRect().top
    const diff = targetContentTop - scrollContentTop

    scrollTargetRef.current?.scrollTo({
      top: diff,
    })

    let newActiveModelIndex = 0
    for (let i = 0; i < variants.length; i++) {
      const modelHeaderId = `model-${i}`
      const modelHeaderScrollTop = document
        .getElementById(modelHeaderId)
        ?.getBoundingClientRect().top

      if (modelHeaderScrollTop <= targetContentTop) {
        newActiveModelIndex = i
      }
    }

    setActiveModelIndex(newActiveModelIndex)
  }

  const maxCardHeight = useMemo(() => {
    if (isMobile || isTablet) {
      return windowHeight - introHeight - imageHeight + 24 - 16
    }

    return windowHeight - introHeight - 128 - 32
  }, [windowHeight, introHeight, isMobile, isTablet])

  const cardTop = useMemo(() => {
    if (isMobile || isTablet) {
      return introHeight + imageHeight - 24
    }

    return introHeight + 128
  }, [introHeight, imageHeight, isMobile, isTablet])

  useEffect(() => {
    window.addEventListener('scroll', onScroll)

    setTimeout(() => window.dispatchEvent(new Event('resize')), 1000)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  const containerPaddingBottom = useMemo(
    () => (isMobile || isTablet ? maxCardHeight - 24 : 100),
    [isMobile, isTablet, maxCardHeight],
  )

  return (
    <div
      className="mb-16 md:mb-32"
      style={
        {
          // marginBottom: containerPaddingBottom,
        }
      }
    >
      <StickyBox offsetTop={0}>
        <div
          className="mb-24 relative"
          style={{ paddingBottom: containerPaddingBottom }}
        >
          <div className="container">
            <div ref={introRef} className="row">
              <div
                className="pb-11 u-p ml-5 sm:ml-7 md:ml-8 lg:ml-0"
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            </div>
            <div className="row-type-f2 !justify-end">
              <div className="col-2-type-f2">
                <div
                  ref={imageContainerRef}
                  className="sm:mx-0 -mx-[26px] relative"
                >
                  <img
                    ref={imageRef}
                    src={variants[0].photo[0]?.url}
                    alt=""
                    className="w-full h-auto -z-1 sm:rounded-10 opacity-0"
                  />
                  {variants.map((variant, index) => (
                    <img
                      key={variant.id}
                      src={variant.photo[0]?.url}
                      alt=""
                      className="absolute top-0 left-0 w-full h-auto transition-all z-10 sm:rounded-5 duration-500"
                      style={{
                        opacity: index === activeModelIndex ? 1 : 0,
                      }}
                    />
                  ))}
                  <div className="absolute top-full left-0 right-0 z-[100] hidden sm:block">
                    <div
                      className="pt-6 flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:pl-6 md:pl-9 sm:pl-6"
                      style={{
                        marginLeft: buttonsLeftMargin,
                      }}
                    >
                      <ButtonLink link={variants[activeModelIndex].button1} />
                      <ButtonLink link={variants[activeModelIndex].button2} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="container absolute left-0 right-0 z-50"
            style={{
              top: cardTop,
            }}
          >
            <div className="row-type-f2">
              <div className="col-1-type-f2 relative">
                <div className="absolute -left-3 sm:left-3 lg:left-0 top-9.5 sm:top-13 lg:top-7 flex flex-col gap-2">
                  {variants.map((variant, index) => (
                    <motion.div
                      key={variant.variantName}
                      className="w-2.5 rounded-full border-2"
                      animate={{
                        backgroundColor:
                          index === activeModelIndex
                            ? 'rgba(0, 0, 0, 1)'
                            : 'rgba(0, 0, 0, 0)',
                        borderColor:
                          index === activeModelIndex
                            ? 'rgba(0, 0, 0, 0)'
                            : 'rgba(0, 0, 0, 1)',
                        height: index === activeModelIndex ? 40 : 10,
                        transition: {
                          ease: 'easeOut',
                          duration: 0.3,
                        },
                      }}
                    />
                  ))}
                </div>
                <div
                  ref={scrollTargetRef}
                  className="rounded-5 shadow-box bg-white z-50 overflow-hidden ml-2.5 mr-2.5 sm:mr-0 sm:ml-11.5 md:ml-13.5 lg:ml-10"
                  style={{
                    height: imageHeight,
                    maxHeight: maxCardHeight,
                  }}
                >
                  <CardContent
                    variants={variants}
                    activeIndex={activeModelIndex}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </StickyBox>

      <div
        className="container  relative pointer-events-none"
        style={{ paddingBottom: containerPaddingBottom }}
      >
        <div className="row">
          <div className="row-type-f2">
            <div className="col-1-type-f2">
              <div
                ref={scrollContentRef}
                className="rounded-5 shadow-box bg-white -z-1 pointer-events-none overflow-hidden ml-2.5 mr-2.5 sm:mr-0 sm:ml-11.5 md:ml-13.5 lg:ml-10 opacity-0"
                style={{
                  marginTop:
                    -maxCardHeight + 10 < -670 ? -670 : -maxCardHeight + 10,
                }}
              >
                <CardContent
                  variants={variants}
                  activeIndex={activeModelIndex}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default M26VariantConfigurator
